import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/helpers';
import type { SaleProduct } from '../posTypes';

interface PosProductCardProps {
  product: SaleProduct;
  isLoading: boolean;
  onSelect: (product: SaleProduct) => void;
}

const PosProductCard: React.FC<PosProductCardProps> = ({ product, isLoading, onSelect }) => {
  const { t } = useTranslation();
  const isOutOfStock = product.stock <= 0;

  return (
    <button
      type="button"
      disabled={isOutOfStock || isLoading}
      onClick={() => onSelect(product)}
      className="group flex min-h-[96px] items-start gap-2 rounded-xl border border-outline-variant/80 bg-white p-2 text-left transition-colors hover:border-primary/60 hover:bg-primary-fixed/15 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-surface-container-low disabled:opacity-65 disabled:hover:border-outline-variant"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-fixed text-primary">
        <span className="material-symbols-outlined text-[18px]">{product.icon}</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-1 font-label-sm font-semibold text-on-surface">{product.name}</span>
        <span className="mt-0.5 block line-clamp-2 text-[11px] leading-4 text-secondary">{product.variant}</span>
        <span className="mt-1.5 flex items-center justify-between gap-1">
          <strong className="truncate font-mono text-[12px] text-primary">{formatCurrency(product.price)}</strong>
          {!isOutOfStock && !isLoading && (
            <span className="material-symbols-outlined text-[17px] text-primary opacity-0 transition-opacity group-hover:opacity-100">add_circle</span>
          )}
        </span>
        <span className={`mt-0.5 block text-[11px] leading-4 ${isOutOfStock ? 'font-semibold text-error' : 'text-secondary'}`}>
          {isOutOfStock
            ? t('admin.sales.stock.out_of_stock')
            : isLoading
              ? t('admin.sales.stock.checking')
              : t('admin.sales.stock.available', { count: product.stock })}
        </span>
      </span>
    </button>
  );
};

export default PosProductCard;
